// Toplu işlem çubuğu (tasarım D) — Canlı Durum'da "Seçim modu" ile seçilen temsilcilere AYNI
// kademe-1 komutu gönderir ve temsilci başına ilerlemeyi gösterir. Sunucuda toplu uç YOK: her
// temsilciye mevcut tekil POST /api/agents/:id/command (tek-canlı-komut kuralı aynen geçerli).
//
// Sıfırlama/yeniden yükleme gibi kademe 2–3 komutlar toplu verilemez (tasarım: "Resets are
// single-agent only"). Kapalı/karanlık panele komut GÖNDERİLMEZ: eklenti 15 dk'dan eski komutu
// reddeder, "açılınca uygula" yeni sürüm ister — sessizce süresi dolacak bir satır üretmek yerine
// satır "Panel kapalı" der. "Eklentiyi güncelle" de yeni sürüm ister (update_extension), burada yok.

import { useEffect, useRef, useState } from 'react';
import { getAgentCommands, issueAgentCommand } from '../api';
import { isLiveStatus, L, statusBadge, summarizeResult, supportsKind, TOOLKIT_MIN_VERSION, type Lang } from '../ext-health';
import type { LiveAgent } from '../types';
import { Icon, inputStyle } from '../ui';

type Action = 'clear_cache' | 'diagnostics' | 'show_message' | 'update_extension';

interface Row { id: string; name: string; state: 'skip' | 'sending' | 'live' | 'done' | 'error'; cmdId?: string; status?: string; detail: string }

const C = { ok: '#37c46b', warn: '#e0a23c', bad: '#ef5d5d', info: '#3d8bff', none: '#6b7280' };
const pill = (c: string): React.CSSProperties => ({
  fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', color: c,
  background: c + '1a', border: '1px solid ' + c + '40', whiteSpace: 'nowrap', flex: 'none',
});

export default function BulkBar({ agents, lang, onClear }: { agents: LiveAgent[]; lang: Lang; onClear: () => void }) {
  const [action, setAction] = useState<Action | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState('');
  // update_extension = kademe 2 → toplu gönderimden önce tek onay.
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const pending = rows.some((r) => r.state === 'sending' || r.state === 'live');

  // İlerleme: canlı satırların komutunu 4 sn'de bir okur, bitince sonucu yazar.
  useEffect(() => {
    if (!pending) return;
    const t = window.setInterval(async () => {
      const liveRows = rows.filter((r) => r.state === 'live' && r.cmdId);
      const upd = await Promise.all(liveRows.map(async (r) => {
        try {
          const res = await getAgentCommands(r.id);
          const c = res.commands.find((x) => x.id === r.cmdId);
          if (!c) return r;
          if (isLiveStatus(c.status)) return { ...r, status: c.status, detail: summarizeResult(c.kind, c.status, c.result, lang) };
          return { ...r, state: c.status === 'done' ? 'done' as const : 'error' as const, status: c.status, detail: summarizeResult(c.kind, c.status, c.result, lang) };
        } catch { return r; }
      }));
      if (!alive.current) return;
      setRows((prev) => prev.map((r) => upd.find((u) => u.id === r.id) || r));
    }, 4000);
    return () => window.clearInterval(t);
  }, [pending, rows, lang]);

  const run = async (kind: Action, args: Record<string, unknown> = {}) => {
    setAction(kind);
    const init: Row[] = agents.map((a) => (a.panelDark || a.state === 'offline'
      ? { id: a.id, name: a.name, state: 'skip', detail: L(lang, 'Panel kapalı · gönderilmedi', 'Panel closed · not sent') }
      : !supportsKind(a.extVersion, kind)
        ? { id: a.id, name: a.name, state: 'skip', detail: L(lang, 'Sürüm ' + (a.extVersion || '?') + ' · ' + TOOLKIT_MIN_VERSION + '+ gerekli', 'Version ' + (a.extVersion || '?') + ' · needs ' + TOOLKIT_MIN_VERSION + '+') }
        : { id: a.id, name: a.name, state: 'sending', detail: L(lang, 'Gönderiliyor…', 'Sending…') }));
    setRows(init);
    const out = await Promise.all(init.map(async (r): Promise<Row> => {
      if (r.state === 'skip') return r;
      try {
        const res = await issueAgentCommand(r.id, kind, args);
        return { ...r, state: 'live', cmdId: res.command.id, status: 'pending', detail: summarizeResult(kind, 'pending', null, lang) };
      } catch (e) {
        const code = e instanceof Error ? e.message : '';
        return { ...r, state: 'error', detail: code === 'command_in_flight' ? L(lang, 'Başka bir komut bekliyor', 'Another command is pending') : L(lang, 'Gönderilemedi', 'Not sent') + (code ? ' · ' + code : '') };
      }
    }));
    if (alive.current) setRows(out);
  };

  const sendMsg = () => {
    const text = msg.trim();
    if (!text) return;
    setMsgOpen(false);
    setMsg('');
    void run('show_message', { text: text.slice(0, 280) });
  };

  const done = rows.filter((r) => r.state === 'done').length;
  const waiting = rows.filter((r) => r.state === 'sending' || r.state === 'live').length;
  const failed = rows.filter((r) => r.state === 'error' || r.state === 'skip').length;
  const ACTIONS: { k: Action; icon: string; label: string }[] = [
    { k: 'clear_cache', icon: 'cached', label: 'Önbelleği temizle' },
    { k: 'show_message', icon: 'chat', label: 'Mesaj gönder' },
    { k: 'diagnostics', icon: 'troubleshoot', label: 'Durum raporu al' },
    { k: 'update_extension', icon: 'system_update_alt', label: 'Eklentiyi güncelle' },
  ];
  const actionLabel = action ? ACTIONS.find((x) => x.k === action)?.label || '' : '';

  return (
    <>
      {rows.length > 0 && action && (
        <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: '86px', width: 'min(440px, calc(100vw - 32px))', maxHeight: '45vh', overflowY: 'auto', zIndex: 70, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: '12px', boxShadow: '0 18px 44px rgba(0,0,0,.5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 14px', borderBottom: '1px solid var(--border2)' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', flex: 1 }}>{actionLabel}</span>
            <span style={{ fontSize: '11px', color: 'var(--dim)' }}>
              {L(lang, done + ' tamam · ' + waiting + ' bekliyor · ' + failed + ' olmadı', done + ' done · ' + waiting + ' waiting · ' + failed + ' not done')}
            </span>
            {!pending && (
              <button onClick={() => { setRows([]); setAction(null); }} style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'var(--faint)', cursor: 'pointer' }}>
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
          {rows.map((r) => {
            const sb = r.state === 'skip' ? { text: L(lang, 'Kapalı', 'Closed'), level: 'none' as const }
              : r.state === 'sending' ? { text: L(lang, 'Gönderiliyor', 'Sending'), level: 'info' as const }
              : r.status ? statusBadge(r.status, lang) : { text: L(lang, 'Olmadı', 'Not done'), level: 'bad' as const };
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ width: '90px', flex: 'none', fontSize: '12px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: '11.5px', color: 'var(--dim)' }}>{r.detail}</span>
                <span style={pill(C[sb.level])}>{sb.text}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: '20px', zIndex: 71, display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px 9px 16px', width: 'max-content', maxWidth: 'calc(100vw - 32px)', flexWrap: 'wrap', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: '14px', boxShadow: '0 18px 44px rgba(0,0,0,.5)' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{L(lang, agents.length + ' seçili', agents.length + ' selected')}</span>
        <button onClick={onClear} disabled={pending} style={{ padding: '5px 8px', background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: '12px', fontWeight: 600, cursor: pending ? 'not-allowed' : 'pointer' }}>Temizle</button>
        <span style={{ width: '1px', height: '22px', background: 'var(--border2)', margin: '0 4px' }} />
        {confirmUpdate ? (
          <>
            <span style={{ fontSize: '12px', color: 'var(--text)', whiteSpace: 'nowrap' }}>
              {L(lang, 'Güncelleme varsa paneller yeniden yüklenir. Sonuç girenler reddeder.', 'Panels with an update reload. Agents entering a result refuse.')}
            </span>
            <button onClick={() => { setConfirmUpdate(false); void run('update_extension'); }} style={{ padding: '8px 12px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Güncelle</button>
            <button onClick={() => setConfirmUpdate(false)} style={{ padding: '8px 10px', background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Vazgeç</button>
          </>
        ) : msgOpen ? (
          <>
            <input
              autoFocus
              value={msg}
              maxLength={280}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendMsg(); if (e.key === 'Escape') setMsgOpen(false); }}
              placeholder="Temsilcilerin panelinde görünecek mesaj"
              style={{ ...inputStyle, width: '260px', padding: '8px 11px', fontSize: '12.5px' }}
            />
            <button onClick={sendMsg} disabled={!msg.trim()} style={{ padding: '8px 12px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, border: 'none', background: msg.trim() ? 'var(--accent)' : 'var(--border)', color: msg.trim() ? '#fff' : 'var(--placeholder)', cursor: msg.trim() ? 'pointer' : 'not-allowed' }}>Gönder</button>
            <button onClick={() => setMsgOpen(false)} style={{ padding: '8px 10px', background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Vazgeç</button>
          </>
        ) : (
          ACTIONS.map((x) => {
            const disabled = pending || agents.length === 0;
            const on = action === x.k && rows.length > 0;
            return (
              <button
                key={x.k}
                disabled={disabled}
                onClick={() => (x.k === 'show_message' ? setMsgOpen(true) : x.k === 'update_extension' ? setConfirmUpdate(true) : void run(x.k))}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap',
                  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled && !on ? 0.5 : 1,
                  ...(on ? { background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' } : { background: 'transparent', border: '1px solid var(--border2)', color: 'var(--text)' }),
                }}
              >
                <Icon name={x.icon} size={17} />
                <span>{x.label}</span>
              </button>
            );
          })
        )}
        <span style={{ width: '1px', height: '22px', background: 'var(--border2)', margin: '0 4px' }} />
        <span style={{ fontSize: '11px', color: 'var(--faint)', paddingRight: '6px', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
          <Icon name="lock" size={14} />
          <span>Sıfırlamalar yalnız tek temsilciye</span>
        </span>
      </div>
    </>
  );
}
